// components/DemoSpotlight.js
import React, { useState, useEffect } from "react";
import { X, ChevronDown } from "lucide-react";
import { Box, Button, Typography } from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7), 0 0 30px rgba(239, 68, 68, 0.5);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 15px rgba(239, 68, 68, 0), 0 0 50px rgba(239, 68, 68, 0.8);
  }
`;

const modalBounce = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(0.9);
    opacity: 0;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.05);
  }
  100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const bounce = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(10px);
  }
`;

const Backdrop = styled(Box)(() => ({
  position: "fixed",
  inset: 0,
  zIndex: 9999,
  pointerEvents: "auto",
  backgroundColor: "transparent",
}));

const SpotlightContainer = styled(Box)(() => ({
  position: "fixed",
  inset: 0,
  zIndex: 10000,
  pointerEvents: "none",
}));

const ModalBox = styled(Box)(({ theme }) => ({
  position: "fixed",
  left: "50%",
  zIndex: 10001,
  background:
    "linear-gradient(135deg, rgba(249, 115, 22, 0.95) 0%, rgba(239, 68, 68, 0.95) 100%)",
  borderRadius: theme.spacing(3),
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
  padding: theme.spacing(3),
  paddingTop: theme.spacing(4),
  maxWidth: "320px",
  width: "calc(100% - 64px)",
  textAlign: "center",
  pointerEvents: "auto",
  animation: `${modalBounce} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)`,
  border: "3px solid rgba(255, 255, 255, 0.3)",
  overflow: "hidden",
  backdropFilter: "blur(10px)",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "200%",
    height: "100%",
    background:
      "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
    animation: `${shimmer} 3s infinite`,
  },
}));

const CloseButton = styled(Button)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1),
  right: theme.spacing(1),
  minWidth: 0,
  width: 40,
  height: 40,
  borderRadius: "50%",
  backgroundColor: "rgba(255, 255, 255, 0.2)",
  color: "#fff",
  padding: 0,
  transition: "all 0.2s ease",
  zIndex: 1,
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.3)",
    transform: "scale(1.1) rotate(90deg)",
  },
}));

const Title = styled(Typography)(() => ({
  fontSize: "1.75rem",
  fontWeight: 900,
  color: "#fff",
  marginBottom: "12px",
  textShadow: "0 2px 10px rgba(0,0,0,0.3)",
  position: "relative",
  zIndex: 1,
}));

const Emoji = styled("span")(() => ({
  fontSize: "2.5rem",
  display: "block",
  marginBottom: "8px",
  animation: "wave 1s ease-in-out infinite",
  "@keyframes wave": {
    "0%, 100%": {
      transform: "rotate(0deg)",
    },
    "25%": {
      transform: "rotate(20deg)",
    },
    "75%": {
      transform: "rotate(-20deg)",
    },
  },
}));

const Description = styled(Typography)(() => ({
  fontSize: "0.95rem",
  color: "#fff",
  lineHeight: 1.5,
  fontWeight: 500,
  textShadow: "0 1px 3px rgba(0,0,0,0.2)",
  position: "relative",
  zIndex: 1,
  marginBottom: "16px",
}));

const ArrowContainer = styled(Box)(() => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  animation: `${bounce} 1.5s ease-in-out infinite`,
  position: "relative",
  zIndex: 1,
}));

const ButtonHighlight = styled(Box)(() => ({
  position: "fixed",
  zIndex: 9998,
  pointerEvents: "none",
  borderRadius: "16px",
  border: "6px solid #ef4444",
  animation: `${pulse} 1.5s ease-in-out infinite`,
  boxSizing: "border-box",
}));

export default function DemoSpotlight({ buttonPosition }) {
  const [showSpotlight, setShowSpotlight] = useState(false);

  useEffect(() => {
    const hasSeenSpotlight = document.cookie
      .split("; ")
      .find((row) => row.startsWith("demo_spotlight_seen="));

    const hasSeenDemo = document.cookie
      .split("; ")
      .find((row) => row.startsWith("foodie_call_demo_seen="));

    // Only show spotlight if they haven't seen it AND haven't done the demo
    if (
      !hasSeenSpotlight &&
      !hasSeenDemo &&
      buttonPosition.width > 0 &&
      buttonPosition.top > 0
    ) {
      setTimeout(() => setShowSpotlight(true), 500);
    }
  }, [buttonPosition.width, buttonPosition.top]);

  // Block scrolling when spotlight is shown
  useEffect(() => {
    if (showSpotlight) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showSpotlight]);

  const handleClose = () => {
    const expiryDate = new Date();
    expiryDate.setFullYear(expiryDate.getFullYear() + 1);
    document.cookie = `demo_spotlight_seen=true; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
    setShowSpotlight(false);
  };

  if (!showSpotlight || !buttonPosition.width || buttonPosition.top <= 0)
    return null;

  const borderWidth = 6;
  const padding = 4;
  const totalOffset = padding + borderWidth / 2;

  // Position modal between top of screen and demo button
  const modalTop = buttonPosition.top / 2;

  return (
    <>
      {/* Invisible backdrop that blocks all clicks */}
      <Backdrop />

      {/* Red pulsing outline around the demo button */}
      <ButtonHighlight
        style={{
          top: `${buttonPosition.top - totalOffset}px`,
          left: `${buttonPosition.left - totalOffset}px`,
          width: `${buttonPosition.width + totalOffset * 2}px`,
          height: `${buttonPosition.height + totalOffset * 2}px`,
        }}
      />

      {/* Centered modal with message */}
      <SpotlightContainer>
        <ModalBox
          style={{
            top: `${modalTop}px`,
            transform: "translate(-50%, -50%)",
          }}
        >
          <CloseButton onClick={handleClose}>
            <X size={24} strokeWidth={3} />
          </CloseButton>

          <Emoji>👋</Emoji>
          <Title>First time here?</Title>
          <Description>
            Set your party to "TRY DEMO (1 Person)" if you would like to try the
            full Foodie Call experience without needing a partner!
          </Description>
          <ArrowContainer>
            <ChevronDown size={40} color="#fff" strokeWidth={3} />
          </ArrowContainer>
        </ModalBox>
      </SpotlightContainer>
    </>
  );
}
